import {
  Datagrid,
  DateInput,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
  useRecordContext,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { UserLink } from '../../components/EntityLink';
import { auditActionLabel, formatDateTime } from '../../i18n/labels';
import type { AuditEntry } from '../../types';

/**
 * Фильтр по действию — prefix-поиск на backend (startsWith),
 * поэтому категории вида "staff." отбирают все действия сотрудников.
 */
const actionChoices = [
  { id: 'staff.', name: 'Действия сотрудников (все)' },
  { id: 'staff.user_suspended', name: 'Блокировка пользователя' },
  { id: 'staff.user_unsuspended', name: 'Разблокировка пользователя' },
  { id: 'staff.photo_', name: 'Модерация фото' },
  { id: 'staff.report_', name: 'Решения по жалобам' },
  { id: 'staff.invitation_', name: 'Скрытие/восстановление приглашений' },
  { id: 'staff.login_', name: 'Входы сотрудников' },
  { id: 'auth.', name: 'Аутентификация пользователей' },
  { id: 'profile.', name: 'Профили' },
  { id: 'invitation.', name: 'Приглашения' },
  { id: 'like', name: 'Лайки' },
  { id: 'match.', name: 'Матчи' },
  { id: 'chat.', name: 'Чаты' },
  { id: 'meeting.', name: 'Встречи' },
  { id: 'rating.', name: 'Рейтинг' },
  { id: 'user.', name: 'Аккаунты (жалобы, удаления)' },
];

const filters = [
  <SelectInput key="action" source="action" label="Действие" choices={actionChoices} alwaysOn />,
  <TextInput key="userId" source="userId" label="ID пользователя/сотрудника" alwaysOn />,
  <DateInput
    key="from"
    source="from"
    label="С даты"
    parse={(v: string | null) => (v ? `${v}T00:00:00` : v)}
    format={(v: string | null) => (v ? v.slice(0, 10) : v)}
  />,
  <DateInput
    key="to"
    source="to"
    label="По дату"
    parse={(v: string | null) => (v ? `${v}T23:59:59` : v)}
    format={(v: string | null) => (v ? v.slice(0, 10) : v)}
  />,
];

/** Раскрытие строки: полные метаданные записи. */
function AuditLogExpand(): JSX.Element | null {
  const record = useRecordContext<AuditEntry & { ip: string | null }>();
  if (!record) return null;
  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Код действия: {record.action}
        {record.ip ? ` · IP: ${record.ip}` : ''}
      </Typography>
      <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
        {record.metadata ? JSON.stringify(record.metadata, null, 2) : 'Метаданных нет'}
      </pre>
    </Box>
  );
}

export function AuditLogList(): JSX.Element {
  return (
    <List filters={filters} perPage={100} pagination={false}>
      <Datagrid bulkActionButtons={false} rowClick="expand" expand={<AuditLogExpand />}>
        <FunctionField
          label="Время"
          render={(record: { createdAt: string }) => formatDateTime(record.createdAt)}
        />
        <FunctionField
          label="Кто"
          render={(record: { userId: string | null; action: string }) => {
            if (!record.userId) return 'Система';
            // В staff-действиях userId — это id сотрудника, ссылка на профиль
            // пользователя была бы неверной
            if (record.action.startsWith('staff.')) {
              return `Сотрудник ${record.userId.slice(0, 8)}`;
            }
            return <UserLink id={record.userId} />;
          }}
        />
        <FunctionField
          label="Действие"
          render={(record: { action: string }) => auditActionLabel(record.action)}
        />
        <TextField source="resourceType" label="Объект" />
        <TextField source="resourceId" label="ID объекта" />
        <TextField source="ip" label="IP" />
      </Datagrid>
    </List>
  );
}
